// src/components/analysis/detail/FinanceAnalysisDetail.js
import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Divider,
  Grid,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Rating,
  Avatar,
  Chip,
  Alert
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AssessmentIcon from '@mui/icons-material/Assessment';
import StarIcon from '@mui/icons-material/Star';
import PaymentsIcon from '@mui/icons-material/Payments';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import InfoIcon from '@mui/icons-material/Info';

// Helper function to format keys for display
const formatEvaluationKey = (key) => {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

// Custom list item component for better formatting
const EnhancedListItem = ({ children }) => (
  <ListItem 
    sx={{ 
      padding: 0,
      paddingY: 0.5,
      marginLeft: 0
    }}
  >
    <ListItemIcon sx={{ minWidth: 28 }}>
      <CheckCircleOutlineIcon color="primary" fontSize="small" />
    </ListItemIcon>
    <ListItemText 
      primary={
        <Typography variant="body2">
          {children}
        </Typography>
      } 
    />
  </ListItem>
);

// Section header component
const SectionHeader = ({ icon, title }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
    <Avatar sx={{ bgcolor: 'primary.main', mr: 1.5, width: 36, height: 36 }}>
      {icon}
    </Avatar>
    <Typography variant="h6" color="primary.main" fontWeight="bold">
      {title}
    </Typography>
  </Box>
);

// Helper function to render subscription model details
const renderSubscriptionModel = (model) => {
  if (typeof model === 'string') {
    return <Typography variant="body2">{model}</Typography>;
  }
  if (typeof model === 'object' && model !== null && model.details && typeof model.details === 'object') {
    return (
      <List dense disablePadding sx={{ pl: 0 }}>
        {Object.entries(model.details).map(([key, value]) => (
          <ListItem key={key} sx={{ paddingLeft: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Typography variant="body2" component="span" fontWeight="bold">{key}:</Typography>
            <Typography variant="body2" component="span" sx={{ pl: 2 }}>
              {typeof value === 'object' 
                ? `${value.price || ''}${value.includes ? ` (${value.includes})` : ''}` 
                : value.price || value}
            </Typography>
          </ListItem>
        ))}
      </List>
    );
  }
  // Fallback for other object types or unexpected structures
  try {
    return <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(model, null, 2)}</Typography>;
  } catch (e) {
    return <Typography variant="body2" color="error">Invalid subscription model format</Typography>;
  }
};


const FinanceAnalysisDetail = ({ data }) => {
  if (!data || typeof data !== 'object') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Invalid financial data format. Please check the analysis results.
        </Alert>
      </Box>
    );
  }

  // Safely extract data with fallbacks
  const revenueModel = data.revenue_model || {};
  const historicalFinancials = data.historical_financials || {}; // Renamed from financials
  const evaluation = data.evaluation || {};

  // Helper function to safely render object data
  const renderObjectData = (obj) => {
    if (!obj) return null;
    if (typeof obj === 'string') return obj;
    if (Array.isArray(obj)) return obj.join(', ');
    
    try {
      return JSON.stringify(obj, null, 2);
    } catch (error) {
      console.error('Error rendering object:', error);
      return 'Data format not supported';
    }
  };

  return (
    <Box>
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
            <SectionHeader 
              icon={<AttachMoneyIcon fontSize="small" />}
              title="Business Model"
            />
            
            {revenueModel.pricing && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <LocalOfferIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                  Pricing
                </Typography>
                
                {revenueModel.pricing.subscription_model && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                      <PaymentsIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                      Subscription Model:
                    </Typography>
                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                      {renderSubscriptionModel(revenueModel.pricing.subscription_model)}
                    </Box>
                  </Box>
                )}

                {revenueModel.pricing.one_time_fees && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                      <MonetizationOnIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                      One-time Fees:
                    </Typography>
                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                      {typeof revenueModel.pricing.one_time_fees === 'string' ? (
                        <Typography variant="body2">{revenueModel.pricing.one_time_fees}</Typography>
                      ) : (
                        <List dense disablePadding>
                          {Object.entries(revenueModel.pricing.one_time_fees).map(([key, value]) => (
                            <ListItem key={key} sx={{ paddingLeft: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                              <Typography variant="body2" component="span" fontWeight="bold">
                                {key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}:
                              </Typography>
                              {typeof value === 'object' ? (
                                <Box sx={{ pl: 2 }}>
                                  {Object.entries(value).map(([subKey, subValue]) => (
                                    <Typography key={subKey} variant="body2" component="div">
                                      {subKey.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}: {subValue}
                                    </Typography>
                                  ))}
                                </Box>
                              ) : (
                                <Typography variant="body2" component="span" sx={{ pl: 2 }}>
                                  {value}
                                </Typography>
                              )}
                            </ListItem>
                          ))}
                        </List>
                      )}
                    </Box>
                  </Box>
                )}
                
                {revenueModel.pricing.discounts && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                      <LocalOfferIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                      Discounts:
                    </Typography>
                    <Typography variant="body2" sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                      {revenueModel.pricing.discounts}
                    </Typography>
                  </Box>
                )}
                
                {revenueModel.pricing.pricing_positioning && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                      <InfoIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                      Pricing Positioning:
                    </Typography>
                    <Typography variant="body2" sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                      {revenueModel.pricing.pricing_positioning}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
            
            {revenueModel.revenue_streams && (
              <Box>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <TrendingUpIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                  Revenue Streams
                </Typography>
                
                {revenueModel.revenue_streams.recurring && revenueModel.revenue_streams.recurring.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                      <ShowChartIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                      Recurring Revenue:
                    </Typography>
                    <Box sx={{ bgcolor: 'grey.50', borderRadius: 1, p: 2, border: '1px solid', borderColor: 'grey.200' }}>
                      <List dense disablePadding>
                        {revenueModel.revenue_streams.recurring.map((stream, index) => (
                          <EnhancedListItem key={index}>
                            {stream}
                          </EnhancedListItem>
                        ))}
                      </List>
                    </Box>
                  </Box>
                )}
                
                {revenueModel.revenue_streams.non_recurring && revenueModel.revenue_streams.non_recurring.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                      <AttachMoneyIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                      Non-recurring Revenue:
                    </Typography>
                    <Box sx={{ bgcolor: 'grey.50', borderRadius: 1, p: 2, border: '1px solid', borderColor: 'grey.200' }}>
                      <List dense disablePadding>
                        {revenueModel.revenue_streams.non_recurring.map((stream, index) => (
                          <EnhancedListItem key={index}>
                            {stream}
                          </EnhancedListItem>
                        ))}
                      </List>
                    </Box>
                  </Box>
                )}
                
                {revenueModel.revenue_streams.customer_segment_distribution && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                      <InfoIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                      Customer Segment Distribution:
                    </Typography>
                    <Typography variant="body2" sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                      {revenueModel.revenue_streams.customer_segment_distribution}
                    </Typography>
                  </Box>
                )}
                
                {revenueModel.revenue_streams.seasonality && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                      <ShowChartIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                      Seasonality:
                    </Typography>
                    <Typography variant="body2" sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                      {revenueModel.revenue_streams.seasonality}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
            <SectionHeader 
              icon={<AssessmentIcon fontSize="small" />}
              title="Historical Financials & Current Status" // Updated Title
            />
            
            {/* Historical Revenue Section */}
            {historicalFinancials.historical_revenue && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <AttachMoneyIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                  Historical Revenue
                </Typography>
                
                {historicalFinancials.historical_revenue.figures && historicalFinancials.historical_revenue.figures.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                      <AssessmentIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                      Revenue Figures:
                    </Typography>
                    <TableContainer sx={{ border: '1px solid', borderColor: 'grey.200', borderRadius: 1 }}>
                      <Table size="small">
                        <TableHead sx={{ bgcolor: 'grey.100' }}>
                          <TableRow>
                            <TableCell><Typography variant="subtitle2">Period</Typography></TableCell>
                            <TableCell align="right"><Typography variant="subtitle2">Amount</Typography></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {Array.isArray(historicalFinancials.historical_revenue.figures) ? (
                            historicalFinancials.historical_revenue.figures.map((figure, index) => {
                              let periodContent = 'N/A';
                              let amountContent = 'N/A';

                              // Robust check for figure object and its properties
                              if (figure && typeof figure === 'object' && figure !== null) {
                                const periodValue = figure.period;
                                const amountValue = figure.amount;

                                // Process period content
                                try {
                                  if (typeof periodValue === 'string' || typeof periodValue === 'number' || periodValue == null) {
                                    periodContent = periodValue;
                                  } else {
                                    periodContent = JSON.stringify(periodValue); // Stringify if object/other
                                  }
                                } catch (e) {
                                  console.error("Error processing period:", e);
                                  periodContent = 'Error';
                                }

                                // Process amount content robustly to prevent object rendering
                                try {
                                  if (amountValue != null && typeof amountValue === 'object' && !Array.isArray(amountValue)) {
                                    // If it's an object, DO NOT render directly. Show placeholder or stringify.
                                    amountContent = `[Object Data]`; // Safer placeholder
                                    // Or: amountContent = JSON.stringify(amountValue); 
                                  } else if (typeof amountValue === 'string' || typeof amountValue === 'number' || amountValue == null) {
                                    amountContent = amountValue; // Handle primitives and null/undefined
                                  } else {
                                     // Fallback for arrays or other unexpected types
                                    amountContent = `[Unsupported Data Type]`;
                                    // Or: amountContent = JSON.stringify(amountValue);
                                  }
                                } catch (e) {
                                  console.error("Error processing amount:", e, amountValue);
                                  amountContent = '[Error Processing]';
                                }

                                // Render the row with processed content, using ?? for null/undefined safety
                                return (
                                  <TableRow key={index}>
                                    <TableCell>{periodContent ?? 'N/A'}</TableCell>
                                    <TableCell align="right">{amountContent ?? 'N/A'}</TableCell>
                                  </TableRow>
                                );

                              } else {
                                // Handle cases where figure itself is not the expected object
                                return (
                                  <TableRow key={`invalid-${index}`}>
                                    <TableCell colSpan={2}>
                                      <Typography variant="body2" color="error">
                                        Invalid figure data format at index {index}.
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                );
                              }
                            })
                          ) : (
                            <TableRow>
                              <TableCell colSpan={2}>
                                <Typography variant="body2" color="textSecondary">
                                  Historical revenue figures data is not available or not an array.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
                
                {historicalFinancials.historical_revenue.growth_rate && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                      <TrendingUpIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                      Historical Growth Rate:
                    </Typography>
                    <Typography variant="body2" sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                      {historicalFinancials.historical_revenue.growth_rate}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
            
            {/* Historical Profitability Section */}
            {historicalFinancials.historical_profitability && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <MonetizationOnIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                  Historical Profitability
                </Typography>
                
                {historicalFinancials.historical_profitability.gross_margins && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                      <ShowChartIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                      Gross Margins:
                    </Typography>
                    <Typography variant="body2" sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                      {historicalFinancials.historical_profitability.gross_margins}
                    </Typography>
                  </Box>
                )}
                
                {historicalFinancials.historical_profitability.ebitda && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                      <AssessmentIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                      EBITDA:
                    </Typography>
                    <Box sx={{ bgcolor: 'grey.50', borderRadius: 1, p: 2, border: '1px solid', borderColor: 'grey.200' }}>
                      {Array.isArray(historicalFinancials.historical_profitability.ebitda) && historicalFinancials.historical_profitability.ebitda.length > 0 ? (
                        <List dense disablePadding>
                          {historicalFinancials.historical_profitability.ebitda.map((item, index) => {
                            let ebitdaAmountContent = 'N/A';
                            if (item && item.amount != null) {
                              if (typeof item.amount === 'object' && !Array.isArray(item.amount)) {
                                // If it's an object, DO NOT render directly. Show placeholder or stringify.
                                ebitdaAmountContent = `[Object Data]`; // Safer placeholder
                                // Or: ebitdaAmountContent = JSON.stringify(item.amount);
                              } else {
                                // Handle primitives and null/undefined
                                ebitdaAmountContent = item.amount;
                              }
                            }
                            // Ensure final content is a string or primitive
                            const displayAmount = (ebitdaAmountContent === null || ebitdaAmountContent === undefined) ? 'N/A' : String(ebitdaAmountContent);
                            
                            return (
                              <EnhancedListItem key={index}>
                                {`${item.period || 'N/A'}: ${displayAmount}`}
                              </EnhancedListItem>
                            );
                          })}
                        </List>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          EBITDA data not available or in unexpected format.
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
                
                {historicalFinancials.historical_profitability.break_even_achieved && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                      <InfoIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                      Break-even Achieved:
                    </Typography>
                    <Typography variant="body2" sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                      {historicalFinancials.historical_profitability.break_even_achieved}
                    </Typography>
                  </Box>
                )}
                
                {historicalFinancials.historical_profitability.profit_margins && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                      <MonetizationOnIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                      Profit Margins:
                    </Typography>
                    <Typography variant="body2" sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                      {typeof historicalFinancials.historical_profitability.profit_margins === 'string'
                        ? historicalFinancials.historical_profitability.profit_margins
                        : Object.entries(historicalFinancials.historical_profitability.profit_margins)
                            .map(([key, value]) => `${key}: ${value}`)
                            .join(', ')}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* Current Financial Status Section */}
            {historicalFinancials.current_financial_status && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <InfoIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                  Current Financial Status
                </Typography>
                
                {historicalFinancials.current_financial_status.cash_position && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                      <AccountBalanceIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                      Cash Position:
                    </Typography>
                    <Typography variant="body2" sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                      {historicalFinancials.current_financial_status.cash_position}
                    </Typography>
                  </Box>
                )}

                {historicalFinancials.current_financial_status.burn_rate && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                      <ShowChartIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                      Current Burn Rate:
                    </Typography>
                    <Typography variant="body2" sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                      {historicalFinancials.current_financial_status.burn_rate}
                    </Typography>
                  </Box>
                )}

                {historicalFinancials.current_financial_status.key_operational_metrics && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                      <AssessmentIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                      Key Operational Metrics:
                    </Typography>
                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                      {(() => {
                        const metrics = historicalFinancials.current_financial_status.key_operational_metrics;
                        if (Array.isArray(metrics) && metrics.length > 0) {
                          // Handle the array of {metric, value} objects
                          return (
                            <List dense disablePadding>
                              {metrics.map((item, index) => (
                                <EnhancedListItem key={index}>
                                  {`${item.metric || 'Metric'}: ${item.value ?? 'Not specified'}`}
                                </EnhancedListItem>
                              ))}
                            </List>
                          );
                        } else if (metrics && typeof metrics === 'object' && !Array.isArray(metrics)) {
                           // Keep fallback for single object or unexpected object types
                           try {
                             // Attempt to display if it's a simple {metric, value} object
                             if (metrics.metric && metrics.value) {
                               return <Typography variant="body2">{`${metrics.metric}: ${metrics.value}`}</Typography>;
                             }
                             // Otherwise, stringify as a last resort for unexpected objects
                             return <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(metrics, null, 2)}</Typography>;
                           } catch (e) {
                             return <Typography variant="body2" color="error">Invalid metrics format</Typography>;
                           }
                        } else {
                          // Handle strings, numbers, null, undefined, or empty array
                          return <Typography variant="body2">{metrics ? String(metrics) : 'Not specified'}</Typography>;
                        }
                      })()}
                    </Box>
                  </Box>
                )}
              </Box>
            )}
            
            {/* Past Funding Section */}
            {historicalFinancials.past_funding && historicalFinancials.past_funding.previous_rounds && historicalFinancials.past_funding.previous_rounds.length > 0 && (
              <Box>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <AccountBalanceIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                  Past Funding
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                    <InfoIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                    Previous Funding Rounds:
                  </Typography>
                  <Box sx={{ bgcolor: 'grey.50', borderRadius: 1, p: 2, border: '1px solid', borderColor: 'grey.200' }}>
                    <List dense disablePadding>
                      {historicalFinancials.past_funding.previous_rounds.map((round, index) => (
                        <EnhancedListItem key={index}>
                          {`${round.round || 'Round'}: ${round.amount || 'Amount not specified'}`}
                          {round.date && ` (Date: ${round.date})`}
                        </EnhancedListItem>
                      ))}
                    </List>
                  </Box>
                </Box>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
      
      <Divider sx={{ my: 4 }} />
      
      <Typography variant="h5" gutterBottom color="primary" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
        <StarIcon sx={{ mr: 1 }} />
        Financial Evaluation
      </Typography>
      
      <Grid container spacing={3}>
        {Object.entries(evaluation)
          .filter(([key]) => key !== 'overall_financial_transparency') // Updated filter key
          .map(([key, value]) => (
            <Grid item xs={12} sm={6} key={key}>
              <Card 
                variant="outlined" 
                sx={{ 
                  borderWidth: 1, 
                  borderColor: 'primary.light',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 1
                  }
                }}
              >
                <CardHeader 
                  title={formatEvaluationKey(key)} 
                  titleTypographyProps={{ fontWeight: 'bold', variant: 'subtitle1' }}
                  sx={{
                    borderBottom: '1px solid',
                    borderColor: 'grey.200'
                  }}
                  subheader={
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <Rating 
                        value={parseFloat(value.score)} 
                        precision={0.5} 
                        readOnly 
                        emptyIcon={<StarIcon style={{ opacity: 0.3 }} fontSize="inherit" />}
                      />
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          ml: 1, 
                          fontWeight: 'bold',
                          color: 'primary.main'
                        }}
                      >
                        {value.score}/5
                      </Typography>
                    </Box>
                  }
                />
                <CardContent>
                  <Typography variant="body2">
                    {value.comments}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
      </Grid>
      
      {evaluation.overall_financial_transparency && ( // Updated key
        <Box sx={{ mt: 3 }}>
          <Card 
            elevation={2}
            sx={{ 
              borderRadius: 1,
              borderLeft: 4, 
              borderColor: 'primary.main'
            }}
          >
            <CardHeader 
              title="Overall Financial Transparency Assessment" // Updated title
              titleTypographyProps={{ fontWeight: 'bold', variant: 'h6', color: 'primary.main' }}
              avatar={
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <StarIcon />
                </Avatar>
              }
              subheader={
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <Rating 
                    value={parseFloat(evaluation.overall_financial_transparency.score)} // Updated key
                    precision={0.5} 
                    readOnly 
                    sx={{ color: 'primary.main' }}
                    emptyIcon={<StarIcon style={{ opacity: 0.3 }} fontSize="inherit" />}
                  />
                  <Typography variant="body2" sx={{ ml: 1, fontWeight: 'bold', color: 'primary.main' }}>
                    {evaluation.overall_financial_transparency.score}/5 
                  </Typography>
                </Box>
              }
            />
            <CardContent>
              <Typography variant="body1">
                {evaluation.overall_financial_transparency.summary} 
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  );
};

export default FinanceAnalysisDetail;
